/**
 * CECOLOR — current-entity-colour session store (sibling zustand store).
 *
 * # Why a separate store (NOT a numeric sysvar)
 *
 * AutoCAD's CECOLOR system variable holds the CURRENT colour assigned to
 * NEWLY-created entities. Unlike the integer-valued Dynamic-Input sysvars
 * (DYNMODE / OSMODE / MIRRTEXT) the editor keeps in `sysvarStore`
 * (`apps/web/src/app/dynamicInput/sysvarStore.ts`, schema `Record<,number>`),
 * CECOLOR is the typed `EntityColor` UNION (ByLayer / ByBlock / index / true-
 * colour / colour-book). A union cannot ride the integer-only sysvar schema
 * (`isValidSysvarValue` rejects non-ints), so — mirroring the sibling-store
 * pattern — this module is a tiny zustand store with a `useCeColor()` hook for
 * React consumers and a `getCeColor()` getter for non-React callers (the draft-
 * dispatch boundary + the export projection).
 *
 * # Per-drawing, NOT a cross-session profile (round-8 FINDING B)
 *
 * The Autodesk CECOLOR sysvar doc (GUID-703858E3-9038-406A-B499-90A0C05BECBF)
 * lists *Saved in: Drawing*, *Initial value: BYLAYER*. CECOLOR is a PER-DRAWING
 * sysvar, NOT a cross-drawing profile the COLOR command mutates: a NEW drawing
 * takes the template default (ByLayer), and the value is persisted in the DXF
 * `$CECOLOR` HEADER var — not in browser localStorage. So this store is a pure
 * IN-MEMORY mirror of `doc.cecolor`; there is NO cross-session localStorage
 * persist (the round-1 persist, copied from `rendererPreference`, was non-
 * AutoCAD and the root of the round-7/8 reset desync — removed here).
 *
 * # Relationship to `doc.cecolor` (the SINGLE source of truth)
 *
 * `doc.cecolor` (drawing-model `model.ts`) round-trips the DXF file's `$CECOLOR`
 * HEADER var and is the SINGLE source of truth; THIS store MIRRORS it for the
 * live session. The seam is symmetric with MIRRTEXT/OSMODE: hydrate-IN on OPEN /
 * new-doc (`buildCecolorHydration` → `hydrateCeColor`) and project-OUT on export
 * (`applyLiveCeColor`). The `header/set-cecolor` reducer writes `doc.cecolor`;
 * the CLI / ribbon swatch / AI all route through that command (Command-Engine
 * one-truth, invertible undo) AND mirror the value into this store so the
 * create-dispatch boundary stamps new entities. reset/new-drawing → ByLayer for
 * BOTH doc and live (template default — no second source to desync).
 *
 * # Default
 *
 * `{ kind: "byLayer" }` — AutoCAD's out-of-the-box CECOLOR.
 */

import { create } from "zustand";

import type { EditorCommand } from "@dxf-ai-editor/contracts";
import type { EntityColor } from "@dxf-ai-editor/drawing-model";

/** AutoCAD's out-of-the-box CECOLOR — ByLayer. */
export const DEFAULT_CE_COLOR: EntityColor = { kind: "byLayer" };

interface CeColorStore {
  /** The current default colour for newly-created entities. */
  ceColor: EntityColor;
  /**
   * CECOLOR round-8 FINDING B — a monotonic document-reset epoch. The ribbon
   * `CeColorSwatch` keeps its "Select Color" picker open-state LOCAL (component
   * `useState`), so `resetShellState()` — which closes the COMMAND-LINE picker
   * via `setCeColorPickerOpen(false)` — has no reach into the swatch's portal.
   * On reset / new-drawing this epoch is bumped ({@link bumpResetEpoch}); the
   * swatch subscribes to it (it already subscribes to this store via
   * `useCeColor`) and closes its local picker on change, so a stale picker can
   * no longer stay mounted over the fresh ByLayer drawing and apply a colour.
   */
  resetEpoch: number;
  /**
   * CECOLOR round-9 — a monotonic "colour committed via the ribbon swatch" epoch.
   * The dialog-less `-COLOR` keyword prompt arms `colorPromptPending` in App; the
   * pending-reply consumer at the top of `submitCommandText` then swallows the
   * NEXT command-line submission as a colour reply. If the user instead picks a
   * colour through the RIBBON SWATCH while that prompt is armed, the swatch
   * commit applies the colour (the prompt is satisfied/moot) but — being a child
   * component with no reach into App's `colorPromptPending` setter — leaves the
   * flag set, so the next `LINE`/`CIRCLE`/… submission is wrongly eaten as a
   * colour reply. The swatch bumps this epoch on commit ({@link
   * bumpColorCommitEpoch}); App subscribes (it already subscribes to this store)
   * and clears `colorPromptPending` on change — a store SIGNAL with zero new
   * module edges, mirroring the proven {@link resetEpoch} pattern. Distinct from
   * `resetEpoch` so the swatch does NOT re-close its own picker mid-commit and the
   * command-line picker is left untouched (only the pending flag is moot here).
   */
  colorCommitEpoch: number;
  /**
   * Setter for a deliberate forward set (COLOR command / ribbon swatch / AI).
   * In-memory only — CECOLOR persists in the DXF `$CECOLOR` HEADER (per-drawing),
   * NOT in localStorage (round-8 FINDING B). The `header/set-cecolor` command is
   * the source of truth; this mirrors the resulting `doc.cecolor` into the live
   * session so the create-dispatch boundary stamps new entities.
   */
  setCeColor: (next: EntityColor) => void;
  /**
   * Mirror setter — hydrate the live value FROM `doc.cecolor` on OPEN / new-doc
   * (and on undo/redo replay). Semantically identical to {@link setCeColor} now
   * that neither persists; kept as a distinct name for call-site intent.
   */
  hydrateCeColor: (next: EntityColor) => void;
  /**
   * Bump {@link resetEpoch} so reset-aware subscribers (the ribbon
   * `CeColorSwatch`) close any open transient UI on document reset / new-drawing.
   * Called from `resetShellState()` alongside the command-line picker close.
   */
  bumpResetEpoch: () => void;
  /**
   * Bump {@link colorCommitEpoch} so the App-level subscriber clears
   * `colorPromptPending` when a colour is committed through the ribbon swatch
   * (the armed `-COLOR` prompt is satisfied/moot once the swatch applied a
   * colour). Called from `CeColorSwatch.applyColor` after the colour dispatch.
   */
  bumpColorCommitEpoch: () => void;
  reset: () => void;
}

/** Raw zustand store handle — used directly by tests / non-React getters. */
export const useCeColorStore = create<CeColorStore>((set) => ({
  ceColor: { ...DEFAULT_CE_COLOR },
  resetEpoch: 0,
  colorCommitEpoch: 0,
  setCeColor: (next) => {
    set({ ceColor: next });
  },
  hydrateCeColor: (next) => {
    // Mirror the file's / document's $CECOLOR into the live session (OPEN /
    // new-doc / undo-redo replay). In-memory only — no cross-session persist.
    set({ ceColor: next });
  },
  bumpResetEpoch: () => {
    // round-8 FINDING B — monotonic bump so the ribbon swatch closes its local
    // picker on document reset / new-drawing (its open-state is component-local
    // and out of reach of resetShellState's command-line picker close).
    set((prev) => ({ resetEpoch: prev.resetEpoch + 1 }));
  },
  bumpColorCommitEpoch: () => {
    // round-9 — monotonic bump so the App-level subscriber clears
    // `colorPromptPending` when a colour is committed through the ribbon swatch
    // (App's pending flag is out of reach of this child component; a store
    // signal mirrors the resetEpoch pattern with zero new module edges).
    set((prev) => ({ colorCommitEpoch: prev.colorCommitEpoch + 1 }));
  },
  reset: () => {
    // Bump the epoch here too so a direct store `reset()` (not only the
    // resetShellState path) also closes any reset-aware transient UI.
    set((prev) => ({
      ceColor: { ...DEFAULT_CE_COLOR },
      resetEpoch: prev.resetEpoch + 1,
    }));
  },
}));

/** Identity selector — returns the whole store (the no-argument hook form). */
const selectWholeStore = (store: CeColorStore): CeColorStore => store;

/**
 * React hook — returns either the whole store (no argument) or a selected slice
 * (selector argument). Mirrors the zustand idiom used by `useRendererPreference`
 * so the sibling stores look the same to consumers. Unlike that template, the
 * underlying store hook is called UNCONDITIONALLY (the selector is normalised to
 * an identity selector first) so React's rules-of-hooks lint stays clean.
 */
export function useCeColor(): CeColorStore;
export function useCeColor<T>(selector: (store: CeColorStore) => T): T;
export function useCeColor<T>(
  selector?: (store: CeColorStore) => T,
): CeColorStore | T {
  return useCeColorStore(selector ?? (selectWholeStore as (s: CeColorStore) => T));
}

/**
 * Pure getter for non-React callers (the create-dispatch boundary stamps this
 * onto new entities; the export projection reads it). Mirrors
 * `getRendererPreference()`.
 */
export function getCeColor(): EntityColor {
  return useCeColorStore.getState().ceColor;
}

/** Imperative setter for non-React callers (the COLOR command, the AI sync). */
export function setCeColor(next: EntityColor): void {
  useCeColorStore.getState().setCeColor(next);
}

/**
 * CECOLOR — the `geometry/create-*` command types that inherit the current
 * colour. New entities of these types are stamped with the live `getCeColor()`
 * at the single dispatch chokepoint ({@link stampCeColorOntoCreateCommand},
 * called from `editorStore.dispatch`) so EVERY create path converges on one
 * truth: the interactive draft, the command-line `LINE`/`CIRCLE`/… text path,
 * and the AI base-primitive contour all inherit CECOLOR without a per-path
 * stamp. Each listed type carries the optional `color` field on its create
 * contract AND its geometry-reducer case stamps `command.color ?? ByLayer`.
 *
 * # Exhaustive seam-completeness audit (FINDING 3 — enumerated against the
 *   `geometry/create-*` arm of the dispatcher allowlist `storeHelpers.ts`)
 *
 * INCLUDED (a persisted drawn entity that inherits CECOLOR in AutoCAD):
 *   line / point / circle / arc / ellipse / spline / lwpolyline (also RECTANG /
 *   POLYGON, which route through create-lwpolyline), donut (a closed LWPOLYLINE),
 *   revcloud (Revision Cloud, all 4 modes), wipeout, hatch, text, mtext,
 *   dimension (ALL variants — DIMLINEAR / DIMALIGNED / DIMANGULAR / DIMDIAMETER /
 *   DIMRADIUS / DIM3POINT / DIMORDINATE all route through the single
 *   `geometry/create-dimension`, narrowed by the integer `dimensionType`),
 *   multileader, block-insert, attdef.
 *   - text / mtext / dimension / multileader — AutoCAD applies CECOLOR to ALL
 *     newly-created objects, not only lines. Their STYLE colour (text style,
 *     dimension/leader style) is a FALLBACK that applies only when the entity
 *     colour is ByLayer — so stamping the object colour is parity-correct.
 *   - block-insert — we stamp the INSERT ENTITY colour. A ByLayer insert colour
 *     lets children render with their OWN colours; an explicit CECOLOR on the
 *     INSERT colours its BYBLOCK children with the insert colour (AutoCAD-
 *     correct). We stamp the insert entity colour ONLY and do NOT recurse into
 *     the block definition.
 *   - attdef (round-5 FINDING B) — an ATTDEF authored in BEDIT is a DRAWN entity,
 *     NOT the block DEFINITION container. AutoCAD assigns CECOLOR to it on
 *     creation (COLOR 1 → ATTDEF → red), exactly like TEXT. Distinct from
 *     block-definition (which IS a colour-less container). The toolbar ATTDEF
 *     path omits `color` so the chokepoint stamp reaches it; the reducer coerces
 *     the stamped union through `coerceColor` (idempotent), ByLayer when absent.
 *
 * DELIBERATELY EXCLUDED — each documented (CLAUDE.md priority #1: a create-*
 *   that should NOT inherit CECOLOR carries a reason, not a silent omission).
 *   Round-5 exhaustive re-verification against AutoCAD of EVERY remaining
 *   excluded create-* (the recurring round-3/4 mis-classification churn):
 *   - block-definition — a BLOCK record / container, NOT a drawn instance; its
 *     colour is decided per-INSERT, and the BLOCK record itself is not rendered.
 *     EXCLUDE (definition container). [attdef moved to INCLUDED above.]
 *   - arrayrect / arraypolar / arraypath — ARRAY associative; these CLONE the
 *     SOURCE entities (`sourceEntityIds`). AutoCAD copies inherit the SOURCE
 *     colour, never the current CECOLOR. EXCLUDE (clone-inherits-source).
 *   - viewport — `geometry/create-viewport` creates a paper-space VP FRAME via
 *     the layout/MVIEW tooling. A VIEWPORT entity does carry an entity colour
 *     (its frame/border), but that colour is controlled by the layout VP flow,
 *     NOT the CECOLOR model-drawing path; making the VP-frame colour follow
 *     CECOLOR is a separate render-visible (Category-C) arm. EXCLUDE for this
 *     slice with the citation (deferred to a render-gated PR, consistent with
 *     the deferred-category-C-UI-items policy).
 *   - chain-group / lookup-table — dynamic-block METADATA graph nodes (parameter
 *     chains / lookup tables), not drawn entities and carry no colour. EXCLUDE
 *     (metadata).
 *
 * A CONTRACT-DERIVED guard (`cecolorStore.test.ts` — enumerates the create-*
 * universe from `editorCommandSchema` and asserts every member is stamped OR a
 * documented exclusion) FAILS if a future create-* is added unclassified, so
 * this set stays exhaustive over time rather than drifting behind the contract.
 */
export const CE_COLOR_STAMPABLE_CREATE: ReadonlySet<EditorCommand["type"]> =
  new Set<EditorCommand["type"]>([
    "geometry/create-line",
    "geometry/create-point",
    "geometry/create-circle",
    "geometry/create-arc",
    "geometry/create-ellipse",
    "geometry/create-spline",
    "geometry/create-lwpolyline",
    "geometry/create-donut",
    "geometry/create-revcloud",
    "geometry/create-wipeout",
    "geometry/create-hatch",
    "geometry/create-text",
    "geometry/create-mtext",
    "geometry/create-dimension",
    "geometry/create-multileader",
    "geometry/create-block-insert",
    // T6b — AI-dispatch coalescing batch sibling. Its constituent
    // `create-block-insert` commands share ONE `color` field by construction
    // (see `aiSafeContour.ts`'s grouping predicate) — which, at translation
    // time, is always `undefined` (the AI translator never sets it), so the
    // WHOLE batch relies on this stamp for CECOLOR inheritance exactly like
    // dispatching its K constituents individually would have.
    "geometry/create-block-inserts-batch",
    "geometry/create-attdef",
  ]);

/**
 * Stamp the live current colour onto a stampable `geometry/create-*` command
 * when the caller did NOT already supply an explicit `color` (AutoCAD assigns
 * CECOLOR to a newly-created entity when the command executes). Returns the
 * command UNCHANGED when it is not a stampable create OR an explicit colour is
 * already present (so an AI / programmatic caller that names a colour keeps it).
 *
 * This is the SINGLE create-colour stamp site — driven from `editorStore.dispatch`
 * so the interactive, command-line, and AI create paths all inherit CECOLOR
 * through one seam (no per-caller `getCeColor()` plumbing).
 */
export function stampCeColorOntoCreateCommand(
  command: EditorCommand,
): EditorCommand {
  // CECOLOR round-6 FINDING C — the BLOCK postAction=convert path. The block
  // DEFINITION (`geometry/create-block-definition`) is DELIBERATELY excluded
  // from CE_COLOR_STAMPABLE_CREATE (a BLOCK record is a colour-less container),
  // but its convert variant ALSO appends a replacement INSERT instance — a drawn
  // entity that, like the regular create-block-insert path, must inherit CECOLOR.
  // Stamp the INSERT-only `convertInsertColor` here (NOT the top-level `color`,
  // which would colour the definition) so the convert flow matches the regular
  // insert flow. Only when postAction=convert and the caller did not already
  // supply an explicit convert colour (an AI/programmatic caller that names one
  // keeps it). The definition stays uncoloured by construction.
  if (
    command.type === "geometry/create-block-definition" &&
    (command as { postAction?: unknown }).postAction === "convert" &&
    (command as { convertInsertColor?: unknown }).convertInsertColor ===
      undefined
  ) {
    return { ...command, convertInsertColor: getCeColor() } as EditorCommand;
  }
  if (!CE_COLOR_STAMPABLE_CREATE.has(command.type)) return command;
  if ((command as { color?: unknown }).color !== undefined) return command;
  return { ...command, color: getCeColor() } as EditorCommand;
}
